import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Circle, Layer, Line, Rect, Stage, Text } from "react-konva";
import type { KonvaEventObject } from "konva/lib/Node";
import type { Stage as KonvaStage } from "konva/lib/Stage";
import { toast } from "sonner";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";

import { useDocumentViewport } from "@/features/document-viewport/context";
import type { DocumentViewportValue } from "@/features/document-viewport/types";
import {
  formatPresenceName,
  type Tool,
} from "@/features/rev-collab/rev-collab-session-helpers";
import type {
  AnnotationRecord,
  AnnotationSnapshot,
  CollaboratorPresence,
  DraftState,
  PendingAnnotationDraft,
} from "@/features/rev-collab/rev-collab-types";
import { getViewportContentCenter } from "@/features/rev-collab/rev-collab-viewport-utils";
import { cn } from "@/lib/utils";

type StagePointerEvent = KonvaEventObject<MouseEvent | TouchEvent>;

interface RevCollabViewportStageProps {
  activePage: number;
  tool: Tool;
  setTool: Dispatch<SetStateAction<Tool>>;
  toolColors: Record<Exclude<Tool, "select" | "hand">, string>;
  canEditShapes: boolean;
  annotations: AnnotationRecord[] | undefined;
  collaborators: CollaboratorPresence[];
  userId: string;
  selectedAnnotationId: Id<"revCollabAnnotations"> | null;
  setSelectedAnnotationId: Dispatch<SetStateAction<Id<"revCollabAnnotations"> | null>>;
  pendingAnnotationDraft: PendingAnnotationDraft | null;
  setPendingAnnotationDraft: Dispatch<SetStateAction<PendingAnnotationDraft | null>>;
  clearPendingDraft: () => void;
  resetPendingCommentBody: () => void;
  viewportApiRef: MutableRefObject<DocumentViewportValue | null>;
  onAnnotationDragEnd: (
    annotationId: Id<"revCollabAnnotations">,
    x: number,
    y: number
  ) => Promise<void>;
}

interface StageAnnotationShapesProps {
  annotations: AnnotationRecord[] | undefined;
  canEditShapes: boolean;
  userId: string;
  tool: Tool;
  selectedAnnotationId: Id<"revCollabAnnotations"> | null;
  setSelectedAnnotationId: Dispatch<SetStateAction<Id<"revCollabAnnotations"> | null>>;
  onAnnotationDragEnd: RevCollabViewportStageProps["onAnnotationDragEnd"];
  pageScaleX: number;
  viewport: DocumentViewportValue;
}

function getAnnotationFill(annotation: AnnotationRecord) {
  const rawFill = annotation.style?.fill;
  if (rawFill === "#00000000" || rawFill === "transparent") {
    return undefined;
  }
  return rawFill ?? "#00000000";
}

function StageAnnotationShapes({
  annotations,
  canEditShapes,
  userId,
  tool,
  selectedAnnotationId,
  setSelectedAnnotationId,
  onAnnotationDragEnd,
  pageScaleX,
  viewport,
}: StageAnnotationShapesProps) {
  return (annotations ?? []).map((annotation) => {
    const isAuthor = String(annotation.createdByUserId) === userId;
    const isSelected = selectedAnnotationId === annotation._id;
    const stroke = annotation.style?.stroke ?? "#0ea5e9";
    const fill = getAnnotationFill(annotation);
    const strokeWidth = Math.max(1, (annotation.style?.strokeWidth ?? 0.002) * pageScaleX);

    if (annotation.kind === "rect" || annotation.kind === "highlight") {
      const rect = viewport.transforms.pageRectToScreenRect({
        x: annotation.geometry.x ?? 0,
        y: annotation.geometry.y ?? 0,
        width: annotation.geometry.width ?? 0,
        height: annotation.geometry.height ?? 0,
      });
      return (
        <Rect
          key={annotation._id}
          x={rect.x}
          y={rect.y}
          width={rect.width}
          height={rect.height}
          stroke={isSelected ? "#111827" : stroke}
          fill={fill}
          strokeWidth={isSelected ? strokeWidth + 1 : strokeWidth}
          draggable={canEditShapes && isAuthor && tool === "select"}
          onClick={() => setSelectedAnnotationId(annotation._id)}
          onTap={() => setSelectedAnnotationId(annotation._id)}
          onDragEnd={(event) =>
            void onAnnotationDragEnd(annotation._id, event.target.x(), event.target.y())
          }
        />
      );
    }

    if (annotation.kind === "circle") {
      const center = viewport.transforms.pageToScreenPoint({
        x: annotation.geometry.x ?? 0,
        y: annotation.geometry.y ?? 0,
      });
      return (
        <Circle
          key={annotation._id}
          x={center.x}
          y={center.y}
          radius={(annotation.geometry.radius ?? 0) * pageScaleX}
          stroke={isSelected ? "#111827" : stroke}
          fill={fill}
          strokeWidth={isSelected ? strokeWidth + 1 : strokeWidth}
          draggable={canEditShapes && isAuthor && tool === "select"}
          onClick={() => setSelectedAnnotationId(annotation._id)}
          onTap={() => setSelectedAnnotationId(annotation._id)}
          onDragEnd={(event) =>
            void onAnnotationDragEnd(annotation._id, event.target.x(), event.target.y())
          }
        />
      );
    }

    if (annotation.kind === "pin") {
      const center = viewport.transforms.pageToScreenPoint({
        x: annotation.geometry.x ?? 0,
        y: annotation.geometry.y ?? 0,
      });
      return (
        <Circle
          key={annotation._id}
          x={center.x}
          y={center.y}
          radius={Math.max(5, 6 * viewport.camera.zoom)}
          stroke={isSelected ? "#111827" : stroke}
          fill={fill ?? "#ef4444"}
          strokeWidth={2}
          draggable={canEditShapes && isAuthor && tool === "select"}
          onClick={() => setSelectedAnnotationId(annotation._id)}
          onTap={() => setSelectedAnnotationId(annotation._id)}
          onDragEnd={(event) =>
            void onAnnotationDragEnd(annotation._id, event.target.x(), event.target.y())
          }
        />
      );
    }

    return (
      <Line
        key={annotation._id}
        points={mapNormalizedPointsToScreen(annotation.geometry.points ?? [], viewport)}
        stroke={isSelected ? "#111827" : stroke}
        strokeWidth={isSelected ? strokeWidth + 1 : strokeWidth}
        lineCap="round"
        lineJoin="round"
        onClick={() => setSelectedAnnotationId(annotation._id)}
        onTap={() => setSelectedAnnotationId(annotation._id)}
      />
    );
  });
}

interface DraftPreviewLayerProps {
  draft: DraftState | null;
  pendingAnnotationDraft: PendingAnnotationDraft | null;
  toolColors: Record<Exclude<Tool, "select" | "hand">, string>;
  pageScaleX: number;
  pageScaleY: number;
  viewport: DocumentViewportValue;
}

function DraftPreviewLayer({
  draft,
  pendingAnnotationDraft,
  toolColors,
  pageScaleX,
  pageScaleY,
  viewport,
}: DraftPreviewLayerProps) {
  return (
    <>
      {draft ? (
        draft.kind === "freehand" ? (
          <Line
            points={mapNormalizedPointsToScreen(draft.points, viewport)}
            stroke={toolColors.freehand}
            strokeWidth={2}
            lineCap="round"
            lineJoin="round"
          />
        ) : draft.kind === "circle" ? (
          <Circle
            x={viewport.transforms.pageToScreenPoint({ x: draft.startX, y: draft.startY }).x}
            y={viewport.transforms.pageToScreenPoint({ x: draft.startX, y: draft.startY }).y}
            radius={
              Math.sqrt((draft.x - draft.startX) ** 2 + (draft.y - draft.startY) ** 2) *
              pageScaleX
            }
            stroke={toolColors.circle}
            strokeWidth={2}
          />
        ) : (
          <Rect
            x={viewport.transforms.pageRectToScreenRect({
              x: Math.min(draft.startX, draft.x),
              y: Math.min(draft.startY, draft.y),
              width: Math.abs(draft.startX - draft.x),
              height: Math.abs(draft.startY - draft.y),
            }).x}
            y={viewport.transforms.pageRectToScreenRect({
              x: Math.min(draft.startX, draft.x),
              y: Math.min(draft.startY, draft.y),
              width: Math.abs(draft.startX - draft.x),
              height: Math.abs(draft.startY - draft.y),
            }).y}
            width={Math.abs(draft.startX - draft.x) * pageScaleX}
            height={Math.abs(draft.startY - draft.y) * pageScaleY}
            stroke={toolColors[draft.kind]}
            fill={draft.kind === "highlight" ? `${toolColors.highlight}30` : "#00000000"}
            strokeWidth={2}
          />
        )
      ) : null}

      {pendingAnnotationDraft ? (
        pendingAnnotationDraft.kind === "freehand" ? (
          <Line
            points={mapNormalizedPointsToScreen(pendingAnnotationDraft.geometry.points ?? [], viewport)}
            stroke={toolColors.freehand}
            strokeWidth={2}
            lineCap="round"
            lineJoin="round"
            dash={[6, 4]}
          />
        ) : pendingAnnotationDraft.kind === "circle" ? (
          <Circle
            x={viewport.transforms.pageToScreenPoint({
              x: pendingAnnotationDraft.geometry.x ?? 0,
              y: pendingAnnotationDraft.geometry.y ?? 0,
            }).x}
            y={viewport.transforms.pageToScreenPoint({
              x: pendingAnnotationDraft.geometry.x ?? 0,
              y: pendingAnnotationDraft.geometry.y ?? 0,
            }).y}
            radius={(pendingAnnotationDraft.geometry.radius ?? 0) * pageScaleX}
            stroke={toolColors.circle}
            strokeWidth={2}
            dash={[6, 4]}
          />
        ) : pendingAnnotationDraft.kind === "pin" ? (
          <Circle
            x={viewport.transforms.pageToScreenPoint({
              x: pendingAnnotationDraft.geometry.x ?? 0,
              y: pendingAnnotationDraft.geometry.y ?? 0,
            }).x}
            y={viewport.transforms.pageToScreenPoint({
              x: pendingAnnotationDraft.geometry.x ?? 0,
              y: pendingAnnotationDraft.geometry.y ?? 0,
            }).y}
            radius={Math.max(5, 6 * viewport.camera.zoom)}
            stroke={toolColors.pin}
            fill={toolColors.pin}
            strokeWidth={2}
            opacity={0.9}
          />
        ) : (
          <Rect
            x={viewport.transforms.pageRectToScreenRect({
              x: pendingAnnotationDraft.geometry.x ?? 0,
              y: pendingAnnotationDraft.geometry.y ?? 0,
              width: pendingAnnotationDraft.geometry.width ?? 0,
              height: pendingAnnotationDraft.geometry.height ?? 0,
            }).x}
            y={viewport.transforms.pageRectToScreenRect({
              x: pendingAnnotationDraft.geometry.x ?? 0,
              y: pendingAnnotationDraft.geometry.y ?? 0,
              width: pendingAnnotationDraft.geometry.width ?? 0,
              height: pendingAnnotationDraft.geometry.height ?? 0,
            }).y}
            width={(pendingAnnotationDraft.geometry.width ?? 0) * pageScaleX}
            height={(pendingAnnotationDraft.geometry.height ?? 0) * pageScaleY}
            stroke={toolColors[pendingAnnotationDraft.kind]}
            fill={
              pendingAnnotationDraft.kind === "highlight"
                ? `${toolColors.highlight}30`
                : "#00000000"
            }
            strokeWidth={2}
            dash={[6, 4]}
          />
        )
      ) : null}
    </>
  );
}

function PresenceMarkers({
  activePage,
  collaborators,
  viewport,
}: {
  activePage: number;
  collaborators: CollaboratorPresence[];
  viewport: DocumentViewportValue;
}) {
  return collaborators
    .filter(
      (entry) =>
        entry.data?.pageNumber === activePage &&
        typeof entry.data.cursor?.x === "number" &&
        typeof entry.data.cursor?.y === "number"
    )
    .map((entry) => {
      const cursor = viewport.transforms.pageToScreenPoint({
        x: entry.data?.cursor?.x ?? 0,
        y: entry.data?.cursor?.y ?? 0,
      });
      return (
        <Fragment key={entry.userId}>
          <Circle x={cursor.x} y={cursor.y} radius={4} fill="#8b5cf6" />
          <Text
            x={cursor.x + 8}
            y={cursor.y - 10}
            text={formatPresenceName(entry)}
            fontSize={12}
            fill="#111827"
          />
        </Fragment>
      );
    });
}

function mapNormalizedPointsToScreen(points: number[], viewport: DocumentViewportValue) {
  const nextPoints: number[] = [];
  for (let index = 0; index < points.length; index += 2) {
    const point = viewport.transforms.pageToScreenPoint({
      x: points[index] ?? 0,
      y: points[index + 1] ?? 0,
    });
    nextPoints.push(point.x, point.y);
  }
  return nextPoints;
}

export function RevCollabViewportStage({
  activePage,
  tool,
  setTool,
  toolColors,
  canEditShapes,
  annotations,
  collaborators,
  userId,
  selectedAnnotationId,
  setSelectedAnnotationId,
  pendingAnnotationDraft,
  setPendingAnnotationDraft,
  clearPendingDraft,
  resetPendingCommentBody,
  viewportApiRef,
  onAnnotationDragEnd,
}: RevCollabViewportStageProps) {
  const viewport = useDocumentViewport();
  const stageRef = useRef<KonvaStage | null>(null);
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [spacePressed, setSpacePressed] = useState(false);
  const [panDragStart, setPanDragStart] = useState<{
    pointerX: number;
    pointerY: number;
    panX: number;
    panY: number;
  } | null>(null);

  useEffect(() => {
    viewportApiRef.current = viewport;
  }, [viewport, viewportApiRef]);

  const contentCenter = useMemo(() => getViewportContentCenter(viewport), [viewport]);
  const pageBounds = useMemo(
    () =>
      viewport.transforms.pageRectToScreenRect({
        x: 0,
        y: 0,
        width: 1,
        height: 1,
      }),
    [viewport.transforms]
  );
  const pageScaleX = pageBounds.width;
  const pageScaleY = pageBounds.height;

  const getPointerPosition = useCallback(() => stageRef.current?.getPointerPosition?.() ?? null, []);
  const getStageRelativePoint = useCallback((clientX: number, clientY: number) => {
    const container = stageRef.current?.container();
    if (!container) {
      return null;
    }

    const rect = container.getBoundingClientRect();
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  }, []);

  const isPointInsidePage = useCallback(
    (point: { x: number; y: number }) =>
      point.x >= pageBounds.x &&
      point.x <= pageBounds.x + pageBounds.width &&
      point.y >= pageBounds.y &&
      point.y <= pageBounds.y + pageBounds.height,
    [pageBounds]
  );

  const pointToNormalized = useCallback(
    (point: { x: number; y: number }) =>
      (isPointInsidePage(point) ? viewport.transforms.screenToPagePoint(point) : null),
    [isPointInsidePage, viewport.transforms]
  );

  const openPendingDraft = useCallback(
    (
      kind: Exclude<Tool, "select" | "hand">,
      geometry: AnnotationSnapshot["geometry"],
      anchorPoint: { x: number; y: number }
    ) => {
      setPendingAnnotationDraft({
        kind,
        geometry,
        anchorScreenX: anchorPoint.x,
        anchorScreenY: anchorPoint.y,
      });
      resetPendingCommentBody();
    },
    [resetPendingCommentBody, setPendingAnnotationDraft]
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const isEditableTarget =
        event.target instanceof HTMLElement &&
        (event.target.tagName.toLowerCase() === "input" ||
          event.target.tagName.toLowerCase() === "textarea" ||
          event.target.isContentEditable);

      if (isEditableTarget) {
        if (event.code === "Escape" && pendingAnnotationDraft) {
          event.preventDefault();
          clearPendingDraft();
        }
        return;
      }

      if (event.code === "Space") {
        event.preventDefault();
        setSpacePressed(true);
      }
      if (event.code === "Equal" || event.code === "NumpadAdd") {
        event.preventDefault();
        viewport.scaleZoomAt(1.1, contentCenter);
      }
      if (event.code === "Minus" || event.code === "NumpadSubtract") {
        event.preventDefault();
        viewport.scaleZoomAt(0.9, contentCenter);
      }
      if (event.code === "Digit0") {
        event.preventDefault();
        viewport.fitWidth();
      }
      if (event.code === "Escape" && pendingAnnotationDraft) {
        event.preventDefault();
        clearPendingDraft();
      }
      if (event.code === "KeyV") {
        setTool("select");
      }
      if (event.code === "KeyH") {
        setTool("hand");
      }
      if (event.code === "KeyR") {
        setTool("rect");
      }
      if (event.code === "KeyC") {
        setTool("circle");
      }
      if (event.code === "KeyP") {
        setTool("freehand");
      }
      if (event.code === "KeyI") {
        setTool("pin");
      }
      if (event.code === "BracketLeft" || event.code === "ArrowLeft") {
        event.preventDefault();
        viewport.goToPage(activePage - 1);
      }
      if (event.code === "BracketRight" || event.code === "ArrowRight") {
        event.preventDefault();
        viewport.goToPage(activePage + 1);
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (event.code === "Space") {
        setSpacePressed(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [activePage, clearPendingDraft, contentCenter, pendingAnnotationDraft, setTool, viewport]);

  useEffect(() => {
    if (!panDragStart) {
      return;
    }

    const handleWindowMouseMove = (event: MouseEvent) => {
      const pointer = getStageRelativePoint(event.clientX, event.clientY);
      if (!pointer) {
        return;
      }

      viewport.setPan({
        x: panDragStart.panX + (pointer.x - panDragStart.pointerX),
        y: panDragStart.panY + (pointer.y - panDragStart.pointerY),
      });
    };

    const handleWindowMouseUp = () => {
      setPanDragStart(null);
    };

    window.addEventListener("mousemove", handleWindowMouseMove);
    window.addEventListener("mouseup", handleWindowMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleWindowMouseMove);
      window.removeEventListener("mouseup", handleWindowMouseUp);
    };
  }, [getStageRelativePoint, panDragStart, viewport]);

  const handleStageMouseDown = useCallback(
    (event: StagePointerEvent) => {
      const pointer = getPointerPosition();
      if (!pointer) {
        return;
      }

      const target = event.target;
      const isMiddleMouseDrag = event.evt instanceof MouseEvent && event.evt.button === 1;
      const clickedOnBackground =
        target === target?.getStage?.() || target?.getClassName?.() === "Layer";
      const isPanIntent =
        spacePressed ||
        isMiddleMouseDrag ||
        tool === "hand" ||
        (tool === "select" && clickedOnBackground);

      if (isPanIntent) {
        setPanDragStart({
          pointerX: pointer.x,
          pointerY: pointer.y,
          panX: viewport.camera.pan.x,
          panY: viewport.camera.pan.y,
        });
        return;
      }

      if (pendingAnnotationDraft) {
        return;
      }

      if (!canEditShapes) {
        setSelectedAnnotationId(null);
        return;
      }

      if (tool === "select") {
        if (clickedOnBackground) {
          setSelectedAnnotationId(null);
        }
        return;
      }

      const normalized = pointToNormalized(pointer);
      if (!normalized) {
        return;
      }

      if (tool === "pin") {
        openPendingDraft("pin", { x: normalized.x, y: normalized.y }, pointer);
        return;
      }

      if (tool === "freehand") {
        setDraft({
          kind: "freehand",
          startX: normalized.x,
          startY: normalized.y,
          x: normalized.x,
          y: normalized.y,
          points: [normalized.x, normalized.y],
        });
        return;
      }

      if (tool === "rect" || tool === "highlight" || tool === "circle") {
        setDraft({
          kind: tool,
          startX: normalized.x,
          startY: normalized.y,
          x: normalized.x,
          y: normalized.y,
          points: [],
        });
      }
    },
    [
      canEditShapes,
      getPointerPosition,
      openPendingDraft,
      pendingAnnotationDraft,
      pointToNormalized,
      setSelectedAnnotationId,
      spacePressed,
      tool,
      viewport.camera.pan.x,
      viewport.camera.pan.y,
    ]
  );

  const handleStageMouseMove = useCallback(() => {
    const pointer = getPointerPosition();
    if (!pointer || panDragStart) {
      return;
    }

    const normalized = pointToNormalized(pointer);
    if (!draft || !normalized) {
      return;
    }

    if (draft.kind === "freehand") {
      setDraft((current) =>
        current
          ? {
              ...current,
              x: normalized.x,
              y: normalized.y,
              points: [...current.points, normalized.x, normalized.y],
            }
          : current
      );
      return;
    }

    setDraft((current) =>
      current
        ? {
            ...current,
            x: normalized.x,
            y: normalized.y,
          }
        : current
    );
  }, [draft, getPointerPosition, panDragStart, pointToNormalized]);

  const handleStageMouseUp = useCallback(() => {
    const pointer = getPointerPosition();

    if (panDragStart) {
      setPanDragStart(null);
      return;
    }
    if (!draft) {
      return;
    }

    if (draft.kind === "freehand") {
      if (draft.points.length < 4) {
        setDraft(null);
        return;
      }

      const xValues = draft.points.filter((_, index) => index % 2 === 0);
      const yValues = draft.points.filter((_, index) => index % 2 === 1);
      const centerX =
        xValues.length > 0 ? (Math.min(...xValues) + Math.max(...xValues)) / 2 : draft.startX;
      const centerY =
        yValues.length > 0 ? (Math.min(...yValues) + Math.max(...yValues)) / 2 : draft.startY;
      openPendingDraft(
        "freehand",
        { points: draft.points },
        viewport.transforms.pageToScreenPoint({ x: centerX, y: centerY })
      );
      setDraft(null);
      return;
    }

    if (draft.kind === "circle") {
      const dx = draft.x - draft.startX;
      const dy = draft.y - draft.startY;
      const radius = Math.sqrt(dx * dx + dy * dy);
      if (radius < 0.002) {
        setDraft(null);
        return;
      }

      openPendingDraft(
        "circle",
        { x: draft.startX, y: draft.startY, radius },
        pointer ?? viewport.transforms.pageToScreenPoint({ x: draft.startX, y: draft.startY })
      );
      setDraft(null);
      return;
    }

    const x = Math.min(draft.startX, draft.x);
    const y = Math.min(draft.startY, draft.y);
    const width = Math.abs(draft.startX - draft.x);
    const height = Math.abs(draft.startY - draft.y);
    if (width < 0.002 || height < 0.002) {
      setDraft(null);
      return;
    }

    openPendingDraft(
      draft.kind,
      { x, y, width, height },
      pointer ?? viewport.transforms.pageToScreenPoint({ x: x + width / 2, y: y + height / 2 })
    );
    setDraft(null);
  }, [draft, getPointerPosition, openPendingDraft, panDragStart, viewport.transforms]);

  return (
    <Stage
      ref={stageRef}
      width={viewport.workspaceSize.width}
      height={viewport.workspaceSize.height}
      className={cn(
        "absolute inset-0 touch-none",
        panDragStart
          ? "cursor-grabbing"
          : spacePressed || tool === "hand"
            ? "cursor-grab"
            : tool === "select"
              ? "cursor-default"
              : "cursor-crosshair"
      )}
      onMouseDown={handleStageMouseDown}
      onMouseMove={handleStageMouseMove}
      onMouseUp={handleStageMouseUp}
    >
      <Layer>
        <StageAnnotationShapes
          annotations={annotations}
          canEditShapes={canEditShapes}
          userId={userId}
          tool={tool}
          selectedAnnotationId={selectedAnnotationId}
          setSelectedAnnotationId={setSelectedAnnotationId}
          onAnnotationDragEnd={async (annotationId, x, y) => {
            try {
              await onAnnotationDragEnd(annotationId, x, y);
            } catch (error) {
              console.error(error);
              toast.error("Failed to move annotation");
            }
          }}
          pageScaleX={pageScaleX}
          viewport={viewport}
        />
        <DraftPreviewLayer
          draft={draft}
          pendingAnnotationDraft={pendingAnnotationDraft}
          toolColors={toolColors}
          pageScaleX={pageScaleX}
          pageScaleY={pageScaleY}
          viewport={viewport}
        />
        <PresenceMarkers
          activePage={activePage}
          collaborators={collaborators}
          viewport={viewport}
        />
      </Layer>
    </Stage>
  );
}
